// Wartung math, client twin of utils/maintenance/calc.js (live pass/fail and totals while
// recording; the server stays authoritative). Keep everything below the header identical to the
// server file: test/maintenance/calc.test.js compares both.

const INSTALLATION_TYPES = [
  "heating_gas",
  "heating_oil",
  "heat_pump",
  "electrical_installation",
  "portable_device",
  "smoke_detector",
  "ventilation",
  "roof",
  "fire_protection",
  "other"
];
const ITEM_KINDS = ["check", "measure", "text"];
// What the technician records for an item: ok, not ok (Mangel), not applicable, or nothing yet
const ITEM_STATUSES = ["", "ok", "not_ok", "na"];
const CONTRACT_STATUSES = ["draft", "active", "terminated", "ended"];
const VISIT_STATUSES = ["planned", "in_progress", "completed", "cancelled"];
// per_visit: every visit is invoiced; annual_flat: a recurring flat fee covers the visits
const BILLING_MODES = ["per_visit", "annual_flat"];
const FLAT_INTERVALS = ["monthly", "quarterly", "half_yearly", "yearly"];
// completion: next due = completion day + interval (Prüffrist, DGUV V3);
// schedule: the fixed rhythm from the anchor date is kept (Wartung "jedes Jahr im Oktober")
const DUE_MODES = ["completion", "schedule"];
const TAX_RATES = [19, 7, 0];
const MAX_ITEMS = 120;
const MAX_LINES = 50;
const MAX_CENTS = 100000000;

const pad = n => String(n).padStart(2, "0");

/** A real calendar day "YYYY-MM-DD" (2026-02-30 is not). */
const isDate = value => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) return false;
  const time = Date.parse(`${value}T00:00:00Z`);
  return !Number.isNaN(time) && new Date(time).toISOString().slice(0, 10) === value;
};

const addDays = (iso, days) => {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
};

/** Adds months; a day that does not exist in the target month becomes its last day (31.01. + 1 = 28./29.02.). */
const addMonths = (iso, months) => {
  const [y, m, d] = iso.split("-").map(Number);
  const first = new Date(Date.UTC(y, m - 1 + months, 1));
  const last = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  return `${first.getUTCFullYear()}-${pad(first.getUTCMonth() + 1)}-${pad(Math.min(d, last))}`;
};

const daysBetween = (from, to) =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000);

const clampInterval = months => {
  const n = Math.round(Number(months));
  return Number.isFinite(n) && n >= 1 && n <= 120 ? n : 12;
};

/**
 * The k-th date of a rhythm that started on `anchor`. Always counted from the anchor, so a
 * schedule starting on the 31st returns to the 31st after a short month instead of drifting to
 * the 28th.
 */
const occurrence = (anchor, intervalMonths, k) =>
  addMonths(anchor, clampInterval(intervalMonths) * k);

/**
 * Next due date after an inspection.
 *   mode "completion": completedOn + interval
 *   mode "schedule":   the first date of the anchor's rhythm after both the date that was due
 *                      and the completion day (a visit done early does not move the rhythm, one
 *                      done more than an interval late does not leave it overdue)
 */
const nextDueAfter = ({ mode = "completion", anchor, intervalMonths, lastDue, completedOn }) => {
  const months = clampInterval(intervalMonths);
  if (!isDate(completedOn)) return "";
  if (mode !== "schedule" || !isDate(anchor)) return addMonths(completedOn, months);
  const after = isDate(lastDue) && lastDue > completedOn ? lastDue : completedOn;
  const [ay, am] = anchor.split("-").map(Number);
  const [y, m] = after.split("-").map(Number);
  let k = Math.max(0, Math.floor(((y - ay) * 12 + (m - am)) / months) - 1);
  let date = occurrence(anchor, months, k);
  while (date <= after) {
    k += 1;
    date = occurrence(anchor, months, k);
  }
  return date;
};

/** "overdue" (before today), "due" (within the lead days), "upcoming", or "none" (no date). */
const dueState = (nextDue, today, leadDays = 28) => {
  if (!isDate(nextDue)) return "none";
  if (nextDue < today) return "overdue";
  if (daysBetween(today, nextDue) <= Math.max(0, Number(leadDays) || 0)) return "due";
  return "upcoming";
};

/**
 * Laufzeit and Kündigung of a contract on `today`:
 *   startDate, termMonths (Erstlaufzeit), autoRenew, renewalMonths (0 = runs on indefinitely
 *   after the first term, cancellable at any time with the notice period, as § 309 Nr. 9 BGB
 *   requires for consumers), noticeMonths, endDate (fixed end without renewal),
 *   terminationEffective (end after a notice was given).
 * Returns { termEnd, noticeDeadline, renews, ended } with "" for open-ended values.
 */
const contractTerm = (contract, today) => {
  const start = contract?.startDate;
  if (!isDate(start)) return { termEnd: "", noticeDeadline: "", renews: false, ended: false };
  const notice = Math.max(0, Math.round(Number(contract.noticeMonths) || 0));
  const term = Math.max(1, Math.round(Number(contract.termMonths) || 12));
  if (isDate(contract.terminationEffective)) {
    return {
      termEnd: contract.terminationEffective,
      noticeDeadline: "",
      renews: false,
      ended: contract.terminationEffective < today
    };
  }
  const firstEnd = addDays(addMonths(start, term), -1);
  if (!contract.autoRenew) {
    const end = isDate(contract.endDate) ? contract.endDate : firstEnd;
    return {
      termEnd: end,
      noticeDeadline: "",
      renews: false,
      ended: end < today
    };
  }
  const deadlineFor = end => addDays(addMonths(addDays(end, 1), -notice), -1);
  const renewal = Math.max(0, Math.round(Number(contract.renewalMonths) || 0));
  if (today <= deadlineFor(firstEnd)) {
    return { termEnd: firstEnd, noticeDeadline: deadlineFor(firstEnd), renews: true, ended: false };
  }
  if (renewal === 0) {
    // Indefinite after the first term: notice today ends the contract after the notice period,
    // but never before the first term is over
    const end = notice ? addDays(addMonths(today, notice), -1) : today;
    return {
      termEnd: end > firstEnd ? end : firstEnd,
      noticeDeadline: today,
      renews: true,
      ended: false
    };
  }
  // The term whose notice deadline has not passed yet
  let k = 1;
  let end = addDays(addMonths(start, term + renewal * k), -1);
  while (deadlineFor(end) < today && k < 1200) {
    k += 1;
    end = addDays(addMonths(start, term + renewal * k), -1);
  }
  return { termEnd: end, noticeDeadline: deadlineFor(end), renews: true, ended: false };
};

/**
 * End of a contract when notice is given on `noticeDate`: the end of the running term if the
 * notice deadline is kept, otherwise of the next one.
 */
const terminationDate = (contract, noticeDate) => {
  const term = contractTerm({ ...contract, terminationEffective: "" }, noticeDate);
  return term.termEnd;
};

/** "0,25" / "0.25" / 0.25 → 0.25; empty or invalid → null. */
const parseDecimal = value => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const text = String(value).trim().replace(/\s/g, "");
  if (!/^[-+]?\d*(?:[.,]\d+)?$/.test(text) || !/\d/.test(text)) return null;
  const n = Number(text.replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const limitOf = limit => {
  const min = parseDecimal(limit?.min);
  const max = parseDecimal(limit?.max);
  return { min, max };
};

/** A checklist item as stored in a template or a protocol. */
const normalizeItemDef = raw => {
  const kind = ITEM_KINDS.includes(raw?.kind) ? raw.kind : "check";
  const { min, max } = limitOf(raw?.limit);
  return {
    key: String(raw?.key || "")
      .trim()
      .slice(0, 60),
    section: String(raw?.section || "")
      .trim()
      .slice(0, 120),
    label: String(raw?.label || "")
      .trim()
      .slice(0, 300),
    kind,
    unit:
      kind === "measure"
        ? String(raw?.unit || "")
            .trim()
            .slice(0, 20)
        : "",
    limit: kind === "measure" ? { min, max } : { min: null, max: null },
    hint: String(raw?.hint || "")
      .trim()
      .slice(0, 500),
    required: raw?.required === undefined ? kind !== "text" : Boolean(raw.required)
  };
};

/**
 * Evaluation of one recorded item: "pass", "fail", "na" or "open" (still to do).
 * A measurement outside its limits fails whatever was ticked; one without limits passes unless
 * the technician marked it not ok.
 */
const evaluateItem = item => {
  const status = ITEM_STATUSES.includes(item?.status) ? item.status : "";
  if (status === "na") return "na";
  if (item?.kind === "measure") {
    const value = parseDecimal(item.value);
    if (value === null) return status === "not_ok" ? "fail" : "open";
    const { min, max } = limitOf(item.limit);
    if ((min !== null && value < min) || (max !== null && value > max)) return "fail";
    return status === "not_ok" ? "fail" : "pass";
  }
  if (item?.kind === "text") {
    if (status === "not_ok") return "fail";
    return String(item.value || "").trim() ? "pass" : item.required ? "open" : "na";
  }
  if (status === "ok") return "pass";
  if (status === "not_ok") return "fail";
  return "open";
};

/**
 * Result of a protocol: every item with its evaluation, counts, the defects and the overall
 * result "pass", "fail" (at least one defect) or "incomplete" (a required item is open).
 */
const evaluateProtocol = protocol => {
  const items = (protocol?.items || []).map(item => ({ ...item, evaluation: evaluateItem(item) }));
  const counts = { pass: 0, fail: 0, na: 0, open: 0 };
  items.forEach(item => {
    counts[item.evaluation] += 1;
  });
  const missing = items.filter(item => item.evaluation === "open" && item.required);
  const defects = items.filter(item => item.evaluation === "fail");
  const result = missing.length ? "incomplete" : defects.length ? "fail" : "pass";
  return { items, counts, missing, defects, result };
};

/** Integer a / b rounded half away from zero (b > 0). */
const divRound = (a, b) => {
  const sign = a < 0 ? -1 : 1;
  return sign * Math.floor((Math.abs(a) * 2 + b) / (2 * b));
};

/** A billable line of a visit: description, quantity (≤ 3 decimals), unit, unitPrice cents. */
const normalizeLine = raw => {
  const quantity = Math.round((parseDecimal(raw?.quantity) ?? 0) * 1000) / 1000;
  const unitPrice = Math.round(Number(raw?.unitPrice) || 0);
  return {
    ...(raw?._id ? { _id: raw._id } : {}),
    description: String(raw?.description || "")
      .trim()
      .slice(0, 500),
    quantity: Math.min(Math.max(quantity, 0), 1000000),
    unit: String(raw?.unit || "")
      .trim()
      .slice(0, 20),
    unitPrice: Math.min(Math.max(unitPrice, 0), MAX_CENTS),
    // contract = the per-visit price of the contract, extra = material and additional work
    source: raw?.source === "contract" ? "contract" : "extra"
  };
};

/** Net per line (quantity × unit price, half up to the cent), net, VAT and gross in cents. */
const linesTotal = (lines, taxRate) => {
  const rows = (lines || []).map(raw => {
    // Mongoose subdocuments do not spread into their fields
    const line = raw && typeof raw.toObject === "function" ? raw.toObject() : raw;
    return {
      ...line,
      net: divRound(
        Math.round(Number(line.quantity || 0) * 1000) * Number(line.unitPrice || 0),
        1000
      )
    };
  });
  const net = rows.reduce((sum, row) => sum + row.net, 0);
  const rate = TAX_RATES.includes(Number(taxRate)) ? Number(taxRate) : 19;
  const tax = divRound(net * rate, 100);
  return { lines: rows, net, tax, gross: net + tax, taxRate: rate };
};

export {
  INSTALLATION_TYPES,
  ITEM_KINDS,
  ITEM_STATUSES,
  CONTRACT_STATUSES,
  VISIT_STATUSES,
  BILLING_MODES,
  FLAT_INTERVALS,
  DUE_MODES,
  TAX_RATES,
  MAX_ITEMS,
  MAX_LINES,
  MAX_CENTS,
  isDate,
  addDays,
  addMonths,
  daysBetween,
  occurrence,
  nextDueAfter,
  dueState,
  contractTerm,
  terminationDate,
  parseDecimal,
  normalizeItemDef,
  evaluateItem,
  evaluateProtocol,
  divRound,
  normalizeLine,
  linesTotal
};
